import React, { FC, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { Variant } from './types';
import {
  Arc,
  Dot,
  GradientBackground,
  Icon,
  InnerCircle,
  SvgWrapper,
  Text,
} from './Logo.styled';
import { isTabletOrMobile } from '../../../utils/devices/device';

export interface LogoProps {
  /**
   * Variant on which the style of the logo depends. Options are: `Logo.Variant.LIGHT`, `Logo.Variant.DARK`, `Logo.Variant.ISOTYPE`
   */
  variant: Variant;
  /**
   * Indicates if the logo has animation
   */
  hasAnimation: boolean;
}

const ISOTYPE_VIEWBOX = '0 0 8 8';
const TEXT_VIEWBOX = '0 0 24 8';

const getRandomString = () => uuid().replace(/-/g, '');

const Logo: FC<LogoProps> & { Variant: typeof Variant } = ({
  hasAnimation = false,
  variant = Variant.LIGHT,
}) => {
  const randomIdRef = useRef(getRandomString());
  const randomId = randomIdRef.current;
  const logoContainer = useRef<SVGElement>(null);
  const [animated, setAnimated] = useState(hasAnimation);
  const shouldShowText = variant !== Variant.ISOTYPE;
  const viewBox = variant === Variant.ISOTYPE ? ISOTYPE_VIEWBOX : TEXT_VIEWBOX;

  useEffect(() => {
    const currentLogoContainer = logoContainer?.current;

    currentLogoContainer?.addEventListener('animationend', () => {
      setAnimated(false);
    });

    return () => {
      currentLogoContainer?.removeEventListener('animationend', () => {
        setAnimated(false);
      });
    };
  }, []);

  const handleMouseEnter = () => {
    if (hasAnimation && !animated && !isTabletOrMobile) {
      setAnimated(true);
    }
  };

  return (
    <SvgWrapper
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      onMouseEnter={handleMouseEnter}
      $isAnimated={animated}
      ref={logoContainer}
      data-cy="geniallyLogo"
    >
      <GradientBackground>
        <path
          fill={`url(#paint0_linear_${randomId})`}
          d="M4 .25V4l3.28-1.818A3.748 3.748 0 004 .25z"
        />
        <path
          fill={`url(#paint1_linear_${randomId})`}
          d="M4 4l3.214 1.931c.677-1.13.74-2.584.034-3.806L4 4z"
        />
        <path
          fill={`url(#paint2_linear_${randomId})`}
          d="M3.935 7.749a3.748 3.748 0 003.313-1.874L4 4l-.065 3.749z"
        />
        <path
          fill={`url(#paint3_linear_${randomId})`}
          d="M.72 5.818A3.748 3.748 0 004 7.75V4L.72 5.818z"
        />
        <path
          fill={`url(#paint4_linear_${randomId})`}
          d="M.752 5.875L4 4 .786 2.07a3.748 3.748 0 00-.034 3.805z"
        />
        <path
          fill={`url(#paint5_linear_${randomId})`}
          d="M.752 2.125L4 4 4.065.252 4.003.25a3.747 3.747 0 00-3.25 1.874"
        />
      </GradientBackground>

      <InnerCircle cx="4" cy="4" r="2.96" />

      <Icon>
        <Arc d="M5.896 3.956a.367.367 0 00-.36.294 1.496 1.496 0 01-2.96-.389 1.503 1.503 0 011.45-1.4c.188-.006.369.024.535.082a.357.357 0 00.429-.15l.016-.028a.354.354 0 00-.19-.512 2.227 2.227 0 00-.827-.128 2.241 2.241 0 00-2.15 2.147 2.232 2.232 0 004.42.518.367.367 0 00-.363-.434" />
        <Dot cx="4.61" cy="4.21" r=".48" />
      </Icon>

      {shouldShowText && (
        <Text
          $isLight={variant === Variant.LIGHT}
          d="M10.774 2.88a.227.227 0 01.164-.06c.069 0 .123.02.163.061.04.04.061.094.061.16V4.88c0 .335-.089.587-.267.758-.178.17-.44.256-.787.256-.323 0-.592-.062-.807-.184H9.3a.08.08 0 01-.033-.104l.084-.19a.081.081 0 01.112-.04l.004.002c.107.05.207.09.3.118.093.029.203.043.332.043.413 0 .619-.208.619-.623V4.57a.686.686 0 01-.287.307.889.889 0 01-.449.11.975.975 0 01-.511-.134.905.905 0 01-.348-.381A1.257 1.257 0 019 3.902c0-.215.042-.405.126-.57a.942.942 0 01.347-.383.953.953 0 01.51-.137c.167 0 .314.036.441.107a.705.705 0 01.29.306v-.189c0-.066.02-.118.06-.157zm-.684 1.748a.572.572 0 00.458-.192c.11-.129.165-.307.165-.534 0-.224-.054-.402-.163-.532a.567.567 0 00-.46-.195.584.584 0 00-.464.193c-.112.129-.169.307-.169.534 0 .227.057.405.171.534a.585.585 0 00.462.192zm3.196-1.534c.17.189.255.443.255.763 0 .12-.053.18-.161.18h-1.386c.03.463.252.695.668.695.11 0 .206-.015.287-.045.08-.03.166-.069.256-.117a.864.864 0 01.074-.038c.04-.02.074-.03.1-.03.043 0 .077.017.104.05.027.033.04.075.04.126 0 .087-.053.16-.161.22a1.42 1.42 0 01-.343.139c-.124.033-.243.05-.357.05-.347 0-.62-.102-.82-.304-.201-.202-.301-.477-.301-.827 0-.225.044-.423.132-.597a.972.972 0 01.372-.404 1.04 1.04 0 01.545-.143c.293 0 .525.094.696.282zm-1.104.216a.767.767 0 00-.188.435h1.157a.726.726 0 00-.155-.438.496.496 0 00-.397-.163.547.547 0 00-.417.166zm3.832.385V5.02a.058.058 0 01-.059.058h-.336a.058.058 0 01-.059-.058V3.718c0-.185-.036-.322-.11-.408-.073-.087-.187-.13-.343-.13a.585.585 0 00-.44.17.627.627 0 00-.165.458v1.21a.058.058 0 01-.059.059h-.336a.058.058 0 01-.058-.058V3.036c0-.063.021-.114.065-.155a.23.23 0 01.163-.06c.066 0 .119.02.158.058.038.039.058.09.058.153v.097c0 .024.03.034.045.014a.721.721 0 01.255-.228.909.909 0 01.436-.103c.523 0 .785.294.785.883zm.818-1.307a.263.263 0 100-.525.263.263 0 000 .525zm.227.652c0-.071-.021-.126-.063-.163a.234.234 0 00-.162-.056.241.241 0 00-.163.056c-.044.037-.065.092-.065.163v1.987c0 .028.022.05.05.05h.353a.05.05 0 00.05-.05V3.04zm1.114-.177c.14-.034.276-.051.408-.051.29 0 .506.073.648.22.142.146.213.369.213.668v1.31c0 .037-.03.067-.067.067h-.3a.067.067 0 01-.068-.067v-.314a.63.63 0 01-.253.29.748.748 0 01-.393.1.89.89 0 01-.395-.087.68.68 0 01-.28-.245.633.633 0 01-.1-.35.54.54 0 01.123-.374c.082-.091.218-.157.408-.196.19-.038.45-.058.782-.058h.104v-.13c0-.167-.035-.288-.104-.363-.068-.075-.179-.112-.331-.112-.105 0-.204.013-.297.04a2.23 2.23 0 00-.305.117.44.44 0 01-.17.067.138.138 0 01-.11-.05.185.185 0 01-.043-.125c0-.045.013-.085.039-.119a.404.404 0 01.123-.1c.105-.058.227-.103.368-.138zm.673 1.718a.61.61 0 00.157-.433v-.116h-.081c-.233 0-.413.01-.538.031a.53.53 0 00-.27.106c-.053.05-.08.12-.08.213 0 .107.039.196.116.264a.424.424 0 00.292.104.527.527 0 00.404-.169zm1.576-2.37v2.8c0 .037-.03.066-.066.066h-.322a.065.065 0 01-.065-.065v-2.8c0-.072.02-.126.063-.162a.239.239 0 01.161-.054c.066 0 .12.018.164.054.043.036.065.09.065.162zm.977 0v2.8c0 .037-.029.066-.065.066h-.322a.065.065 0 01-.066-.065v-2.8c0-.072.021-.126.063-.162a.239.239 0 01.161-.054c.066 0 .12.018.164.054.044.036.065.09.065.162zM24 3.024c0 .03-.006.058-.018.085l-1.33 2.824a.211.211 0 01-.079.096.197.197 0 01-.11.034.222.222 0 01-.146-.054.168.168 0 01-.065-.135c0-.023.008-.053.023-.09l.393-.836-.866-1.84a.177.177 0 01-.018-.08c0-.057.024-.105.072-.143a.25.25 0 01.161-.059c.09 0 .154.042.193.126l.695 1.543.691-1.539a.196.196 0 01.189-.13c.056 0 .107.02.15.059a.18.18 0 01.065.139z"
        />
      )}

      <defs>
        <linearGradient
          id={`paint0_linear_${randomId}`}
          x1="3.354"
          x2="5.494"
          y1="2.924"
          y2="3.869"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#F2B344" />
          <stop offset="1" stopColor="#F05480" />
        </linearGradient>
        <linearGradient
          id={`paint1_linear_${randomId}`}
          x1="6.984"
          x2="6.984"
          y1="5.177"
          y2="2.925"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#B156CD" />
          <stop offset="1" stopColor="#F05480" />
        </linearGradient>
        <linearGradient
          id={`paint2_linear_${randomId}`}
          x1="5.514"
          x2="3.219"
          y1="3.899"
          y2="5.151"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#B157CE" />
          <stop offset="1" stopColor="#5DA5FB" />
        </linearGradient>
        <linearGradient
          id={`paint3_linear_${randomId}`}
          x1="1.546"
          x2="3.607"
          y1="6.156"
          y2="7.066"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#74E6A7" />
          <stop offset="1" stopColor="#5DA5FB" />
        </linearGradient>
        <linearGradient
          id={`paint4_linear_${randomId}`}
          x1="1.014"
          x2="1.014"
          y1="2.837"
          y2="5.092"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#C8ED5B" />
          <stop offset="1" stopColor="#74E6A7" />
        </linearGradient>
        <linearGradient
          id={`paint5_linear_${randomId}`}
          x1="2.627"
          x2="4.599"
          y1="3.97"
          y2="3.081"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#C8ED5B" />
          <stop offset="1" stopColor="#F2B344" />
        </linearGradient>
      </defs>
    </SvgWrapper>
  );
};

Logo.Variant = Variant;

export default Logo;
